import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Lightbulb, MessageCircle, Send, X } from 'lucide-react';
import { submitFeedback } from '../feedbackData';
import { trackFeedbackWidget } from '../analytics';
import { useAlerts } from './AlertSystem';
import { APP_TRANSLATIONS } from '../constants/translations';

const FEEDBACK_TYPES = [
  { value: 'general', icon: MessageCircle, labelKey: 'feedbackTypeGeneral', headingKey: 'feedbackHeadingGeneral', placeholderKey: 'feedbackPlaceholderGeneral' },
  { value: 'bug', icon: Bug, labelKey: 'feedbackTypeBug', headingKey: 'feedbackHeadingBug', placeholderKey: 'feedbackPlaceholderBug' },
  { value: 'idea', icon: Lightbulb, labelKey: 'feedbackTypeIdea', headingKey: 'feedbackHeadingIdea', placeholderKey: 'feedbackPlaceholderIdea' },
];

// Minimal client-side throttle to deter drive-by spam. Not a security boundary
// (trivially bypassed) - just cuts down on accidental double-submits and casual abuse.
const SUBMIT_COOLDOWN_MS = 60000;
const LAST_SUBMIT_STORAGE_KEY = 'rivals-feedback-last-submit';
// Scroll distance (px) that counts as a deliberate move, so tiny jitters
// don't flicker the button in and out.
const SCROLL_HIDE_THRESHOLD = 12;
// One invite per visitor: the button spells out what it's for once, after
// they've read half a page or stayed 25 seconds, then shrinks back.
const INVITE_STORAGE_KEY = 'rivals-feedback-invited';
const INVITE_DELAY_MS = 25000;
const INVITE_VISIBLE_MS = 5000;

export default function FeedbackWidget({ lang = 'en' }) {
  const location = useLocation();
  const { pushAlert } = useAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const t = (key) => APP_TRANSLATIONS[lang]?.[key] || APP_TRANSLATIONS.en[key] || key;

  // The floating button sits over page content on narrower screens, so it
  // gets out of the way while the reader scrolls down and comes back on any
  // scroll up, near the top, or at the very bottom of the page.
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) < SCROLL_HIDE_THRESHOLD) return;
      const nearTop = y < 200;
      const atBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 80;
      setHiddenByScroll(y > lastY && !nearTop && !atBottom);
      lastY = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const isHidden = hiddenByScroll && !isOpen;

  const [inviting, setInviting] = useState(false);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(INVITE_STORAGE_KEY)) return undefined;
    } catch {
      return undefined;
    }
    let hideTimer;
    const invite = () => {
      cleanup();
      try { window.localStorage.setItem(INVITE_STORAGE_KEY, '1'); } catch { /* best effort */ }
      setInviting(true);
      hideTimer = window.setTimeout(() => setInviting(false), INVITE_VISIBLE_MS);
    };
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable > 0.5) invite();
    };
    const delayTimer = window.setTimeout(invite, INVITE_DELAY_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    function cleanup() {
      window.clearTimeout(delayTimer);
      window.removeEventListener('scroll', onScroll);
    }
    return () => {
      cleanup();
      window.clearTimeout(hideTimer);
    };
  }, []);

  const activeType = useMemo(
    () => FEEDBACK_TYPES.find((item) => item.value === type) || FEEDBACK_TYPES[0],
    [type]
  );

  // Keep the widget out of the admin CMS - it's a public-facing tool, not an admin one.
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const handleToggleOpen = () => {
    setIsOpen((value) => {
      const next = !value;
      if (next) trackFeedbackWidget('feedback_opened');
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!message.trim() || submitting) return;

    try {
      const lastSubmitAt = Number(window.localStorage.getItem(LAST_SUBMIT_STORAGE_KEY) || 0);
      if (Date.now() - lastSubmitAt < SUBMIT_COOLDOWN_MS) {
        pushAlert({ type: 'info', title: t('feedbackToastTitle'), message: t('feedbackCooldownMessage') });
        return;
      }
    } catch {
      // localStorage unavailable - skip the throttle rather than block submission over it.
    }

    setSubmitting(true);
    try {
      await submitFeedback({ type, message, email, locale: lang });
      trackFeedbackWidget('feedback_submitted', type);
      try {
        window.localStorage.setItem(LAST_SUBMIT_STORAGE_KEY, String(Date.now()));
      } catch {
        // Best-effort throttle only - fine if it can't persist.
      }
      setIsOpen(false);
      setType('general');
      setMessage('');
      setEmail('');
      pushAlert({ type: 'success', title: t('feedbackToastTitle'), message: t('feedbackToastSuccess') });
    } catch (error) {
      pushAlert({ type: 'error', title: t('feedbackToastTitle'), message: t('feedbackToastError') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Named for the same reason as the header: keep it above animating hero cards.
    <div
      className={`pointer-events-none fixed bottom-4 z-[150] transition-[transform,opacity] duration-200 ease-out ${isHidden ? 'translate-y-24 opacity-0' : ''}`}
      // Centred in the empty margin right of the max-w-7xl (80rem) content column,
      // so on wide screens it neither covers cards nor hides in the far corner.
      // Narrow screens have no margin and fall back to the 1rem corner offset.
      style={{ viewTransitionName: 'feedback-widget', right: 'max(1rem, calc((100vw - 80rem) / 4 - 1.75rem))' }}
    >
      <div className="flex justify-end">
        <div className={`flex flex-col items-end gap-3 ${isHidden ? '' : 'pointer-events-auto'}`}>
          {isOpen && (
            <div className="w-[min(92vw,22rem)] rounded-[24px] border border-white/15 bg-slate-900 p-5 shadow-[0_24px_80px_rgba(2,6,23,0.6)] ring-1 ring-cyan-400/10">
              <h2 className="text-lg font-bold text-white">{t(activeType.headingKey)}</h2>

              <div className="mt-4 flex gap-2">
                {FEEDBACK_TYPES.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.value === type;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setType(item.value)}
                      className={`flex flex-1 flex-col items-center gap-1 rounded-2xl border px-2 py-2 text-xs font-semibold transition-colors ${
                        isActive
                          ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-200'
                          : 'border-white/10 bg-white/5 text-ink hover:border-white/20 hover:text-white'
                      }`}
                    >
                      <Icon size={16} />
                      {t(item.labelKey)}
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handleSubmit} className="mt-4 space-y-3">
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={t(activeType.placeholderKey)}
                  rows={4}
                  maxLength={2000}
                  required
                  className="w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/50"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t('feedbackEmailPlaceholder')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/50"
                />
                <button
                  type="submit"
                  disabled={submitting || !message.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/15 px-5 py-2.5 text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-500/25 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send size={16} />
                  {submitting ? t('feedbackSubmitting') : t('feedbackSubmit')}
                </button>
              </form>
            </div>
          )}

          <button
            type="button"
            onClick={handleToggleOpen}
            tabIndex={isHidden ? -1 : undefined}
            className="group flex h-11 items-center rounded-full border border-white/15 bg-panel px-[11px] text-ink shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-colors hover:border-cyan-300/50 hover:text-white"
            aria-label={isOpen ? t('feedbackCloseAria') : t('feedbackOpenAria')}
          >
            {isOpen ? <X size={20} strokeWidth={2.25} /> : <MessageCircle size={20} strokeWidth={2.25} />}
            {/* Hover (or the one-time invite) spells out what the button is for. */}
            {!isOpen && (
              <span
                aria-hidden="true"
                className={`overflow-hidden whitespace-nowrap text-sm font-semibold transition-[max-width,margin] duration-300 ease-out motion-reduce:transition-none ${inviting ? 'ml-2 max-w-[15rem]' : 'max-w-0'} group-hover:ml-2 group-hover:max-w-[15rem] group-focus-visible:ml-2 group-focus-visible:max-w-[15rem]`}
              >
                {t('feedbackInvite')}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
